import React from 'react';
import styled from '@emotion/styled';
import useScrollToFooter from '../../hooks/useScrollToFooter';
import { useLang } from '../../context/LanguageContext';
import { t } from '../../translations';

const Section = styled.section`
  min-height: 100vh;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 140px 50px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;

  @media (max-width: 640px) {
    padding: 110px 20px 80px;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid rgba(217, 119, 87, 0.35);
  border-radius: 100px;
  background: rgba(217, 119, 87, 0.07);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #D97757;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 40px;
`;

const BadgeDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #D97757;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

const Tagline = styled.p`
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(11px, 1.2vw, 13px);
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin: 0 0 36px;
`;

const TaglineWord = styled.span`
  color: #999;
`;

const TaglineSep = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #D97757;
  flex-shrink: 0;
  display: inline-block;
`;

const Heading = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(42px, 6.5vw, 88px);
  font-weight: 700;
  line-height: 1.05;
  color: #0A0A0A;
  margin: 0 0 28px;
  max-width: 860px;
  letter-spacing: -3px;

  span {
    color: #D97757;
  }

  @media (max-width: 640px) {
    letter-spacing: -2px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.75;
  color: #666;
  max-width: 500px;
  margin: 0 0 52px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 64px;

  @media (max-width: 400px) {
    flex-direction: column;
    width: 100%;

    button {
      width: 100%;
    }
  }
`;

const PrimaryButton = styled.button`
  padding: 15px 36px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #FFFFFF;
  background: #D97757;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 200ms ease, transform 150ms ease;

  &:hover {
    background: #E8895F;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.button`
  padding: 15px 36px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  background: transparent;
  border: 1px solid #D0D0D0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 200ms ease, background 200ms ease;

  &:hover {
    border-color: #AAAAAA;
    background: rgba(0,0,0,0.03);
  }
`;

const Features = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const FeaturePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  background: #F5F5F5;
  border: 1px solid #E5E5E5;
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #888;
`;

const Check = styled.span`
  color: #D97757;
  font-size: 12px;
`;

const Intro = () => {
  const scrollToFooter = useScrollToFooter();
  const { lang } = useLang();
  const tr = t[lang].intro;

  return (
    <Section>
      <Container>
        <Tagline>
          {tr.tagline.map((word, i) => (
            <React.Fragment key={i}>
              <TaglineWord>{word}</TaglineWord>
              {i < tr.tagline.length - 1 && <TaglineSep />}
            </React.Fragment>
          ))}
        </Tagline>
        <Heading>
          {tr.heading1}<br />
          {lang === 'pl'
            ? <>ze <span>{tr.heading2.replace('ze ', '')}</span></>
            : <>with <span>{tr.heading2.replace('with ', '')}</span></>
          }
        </Heading>
        <Subtitle>{tr.subtitle}</Subtitle>
        <ButtonGroup>
          <PrimaryButton onClick={() => scrollToFooter()}>
            {tr.cta}
          </PrimaryButton>
          <SecondaryButton onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>
            {tr.services}
          </SecondaryButton>
        </ButtonGroup>
      </Container>
    </Section>
  );
};

export default Intro;
